import type { FormField, InputPreset } from '../models/form';
import { toEnglishNumbers } from './numbers';

export interface InputPresetDefinition {
  id: InputPreset;
  title: string;
  placeholder: string;
  inputType: NonNullable<FormField['inputType']>;
  maxLength: number;
  displayMaxLength?: number;
  inputMode: 'numeric' | 'tel' | 'text';
  autoComplete?: string;
  custom?: NonNullable<NonNullable<FormField['validation']>['custom']>;
  regex?: string;
  message: string;
}

export const INPUT_PRESETS: InputPresetDefinition[] = [
  { id: '', title: 'بدون الگوی آماده', placeholder: '', inputType: 'text', maxLength: 250, inputMode: 'text', message: '' },
  { id: 'iranMobile', title: 'شماره همراه ایران', placeholder: '09123456789', inputType: 'tel', maxLength: 14, inputMode: 'tel', autoComplete: 'tel', regex: '^(?:09\\d{9}|\\+989\\d{9}|00989\\d{9}|989\\d{9})$', message: 'شماره همراه واردشده معتبر نیست.' },
  { id: 'iranLandline', title: 'تلفن ثابت ایران', placeholder: '02112345678', inputType: 'tel', maxLength: 14, inputMode: 'tel', autoComplete: 'tel', message: 'شماره تلفن ثابت واردشده معتبر نیست.' },
  { id: 'iranNationalCode', title: 'کد ملی ایران', placeholder: '0013546789', inputType: 'text', maxLength: 10, inputMode: 'numeric', custom: 'iranianNationalCode', message: 'کد ملی واردشده معتبر نیست.' },
  { id: 'iranIban', title: 'شماره شبا ایران', placeholder: 'IR820540102680020817909002', inputType: 'text', maxLength: 26, displayMaxLength: 32, inputMode: 'text', custom: 'iranianIban', message: 'شماره شبای واردشده معتبر نیست.' },
  { id: 'iranPostalCode', title: 'کد پستی ۱۰ رقمی', placeholder: '1234567890', inputType: 'text', maxLength: 10, inputMode: 'numeric', custom: 'iranianPostalCode', message: 'کد پستی باید ۱۰ رقم معتبر باشد.' },
  { id: 'iranCardNumber', title: 'شماره کارت بانکی', placeholder: '6037997512345678', inputType: 'text', maxLength: 16, displayMaxLength: 19, inputMode: 'numeric', custom: 'iranianCardNumber', message: 'شماره کارت بانکی واردشده معتبر نیست.' }
];

export function getInputPreset(id?: InputPreset): InputPresetDefinition | undefined {
  return INPUT_PRESETS.find((preset) => preset.id === (id ?? ''));
}

export function normalizePresetInput(value: unknown, preset?: InputPreset): string {
  let text = toEnglishNumbers(String(value ?? '')).trim();
  switch (preset) {
    case 'iranMobile':
    case 'iranLandline':
      text = text.replace(/[^0-9+]/g, '');
      break;
    case 'iranNationalCode':
    case 'iranPostalCode':
      text = text.replace(/\D/g, '').slice(0, 10);
      break;
    case 'iranCardNumber':
      text = text.replace(/\D/g, '').slice(0, 16);
      break;
    case 'iranIban':
      text = text.replace(/\s+/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (/^\d/.test(text)) text = `IR${text}`;
      text = text.slice(0, 26);
      break;
    default:
      break;
  }
  return text;
}

export function displayPresetInput(value: unknown, preset?: InputPreset): string {
  const normalized = normalizePresetInput(value, preset);
  if (preset === 'iranCardNumber') return normalized.replace(/(.{4})/g, '$1 ').trim();
  if (preset === 'iranIban') return normalized.replace(/(.{4})/g, '$1 ').trim();
  return normalized;
}

export function isIranianIban(value: string): boolean {
  const normalized = normalizePresetInput(value, 'iranIban');
  if (!/^IR\d{24}$/.test(normalized)) return false;
  const rearranged = `${normalized.slice(4)}${normalized.slice(0, 4)}`;
  let remainder = 0;
  for (const character of rearranged) {
    const chunk = /[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character;
    for (const digit of chunk) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

export function isIranianPostalCode(value: string): boolean {
  const normalized = normalizePresetInput(value, 'iranPostalCode');
  return /^\d{10}$/.test(normalized) && !/^(\d)\1{9}$/.test(normalized);
}

export function isIranianCardNumber(value: string): boolean {
  const normalized = normalizePresetInput(value, 'iranCardNumber');
  if (!/^\d{16}$/.test(normalized) || /^(\d)\1{15}$/.test(normalized)) return false;
  let sum = 0;
  for (let index = 0; index < 16; index += 1) {
    const digit = Number(normalized[index]);
    let weighted = digit * (index % 2 === 0 ? 2 : 1);
    if (weighted > 9) weighted -= 9;
    sum += weighted;
  }
  return sum % 10 === 0;
}
